import React, { FC, useMemo, useState } from 'react';
import { Delete } from '@mui/icons-material';
import { IconButton, TableCell, TableRow } from '@mui/material';
import { SetDeviceResponse } from 'api/Responses/SetResponses';
import Checkbox from 'components/Checkbox/Checkbox';
import ConfirmModal from 'components/ConfirmModal/ConfirmModal';
import { useLanguage } from 'context/AppContext';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { LoggedInUser, useUser } from 'store/user/useUser';
import { StorageType } from 'utils/helpers/image.helpers';
import { getRegionSpecificAIR, getRegionSpecificDescription } from 'utils/helpers/user.helpers';
import * as styles from './PartRow.styles';

interface PartRowProps {
  trayID: string;
  partID: string;
  part: SetDeviceResponse;
  dispatchDraft: React.Dispatch<SetAction>;
  disabled: boolean;
  selected: boolean;
  onToggleSelected: (partID: string) => void;
}

const PartRow: FC<PartRowProps> = ({ trayID, partID, part, dispatchDraft, disabled, selected, onToggleSelected }) => {
  const [language] = useLanguage();
  const [open, setOpen] = useState(false);
  const user = useUser() as LoggedInUser;

  const additionalInspectionText = useMemo(() => {
    return getRegionSpecificAIR(user.region, part.part_test_metadata) ?? '';
  }, [part.part_test_metadata, user.region]);

  const partDescription = useMemo(() => {
    const setPartDescription =
      getRegionSpecificDescription(user.region, part.language_metadata) ?? part.description ?? '';
    return setPartDescription;
  }, [part.description, part.language_metadata, user.region]);

  return (
    <>
      <TableRow key={part.code}>
        {!disabled && (
          <TableCell padding="checkbox">
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelected(partID)}
              inputProps={{ 'aria-label': `${language.DeviceTable.selectRow} ${part.code}` }}
            />
          </TableCell>
        )}
        <TableCell>
          <styles.Input
            value={part.ref}
            variant="outlined"
            type="text"
            onChange={e => dispatchDraft(draftActions.partRef(e.target.value, trayID, partID))}
            disabled={disabled}
          />
        </TableCell>

        <TableCell align="center">
          <styles.AsyncImageThumbnail
            src={part.part_image_file}
            alt={part.ref}
            isEnlargedImageAvailable={true}
            imgWidth={3}
            imgHeight={3}
            storageType={StorageType.FIREBASE}
          />
        </TableCell>

        <TableCell>{part.code}</TableCell>

        {/* `title` prop used here so the entire description can be viewed on-hover, in case its too long for the table. */}
        <TableCell title={partDescription}>{partDescription}</TableCell>

        <TableCell>
          <styles.Input
            value={part.qty}
            variant="outlined"
            type="number"
            onChange={e => dispatchDraft(draftActions.partQuantity(e.target.value, trayID, partID))}
            disabled={disabled}
          />
        </TableCell>

        <TableCell align="center">
          <Checkbox checked={!!part.part_test_metadata?.debris_test} disabled={true} />
        </TableCell>

        <TableCell align="center">
          <Checkbox checked={!!part.part_test_metadata?.functional_test} disabled={true} />
        </TableCell>

        <TableCell align="center">
          <Checkbox checked={!!part.part_test_metadata?.torque_test} disabled={true} />
        </TableCell>

        <TableCell align="center">
          <Checkbox checked={!!part.prone_to_debris} disabled={true} />
        </TableCell>

        <TableCell title={additionalInspectionText}>{additionalInspectionText}</TableCell>

        <TableCell className="actions" align="center">
          <IconButton onClick={() => setOpen(true)} disabled={disabled}>
            <Delete />
          </IconButton>
        </TableCell>

        <ConfirmModal
          open={open}
          close={() => setOpen(false)}
          confirm={() => dispatchDraft(draftActions.partDelete(trayID, partID))}
          confirmText={language.ConfirmModal.device}
          buttonText={language.ConfirmModal.delete}
        />
      </TableRow>
    </>
  );
};

const MemoizedPartRow = React.memo(PartRow);

export default MemoizedPartRow;
